import {
  AttributeName,
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
  HeavyVehicleFlowType,
  NumberOfLanesType,
  RiskFactor,
  Score,
  TreatmentType,
} from "@keita/lanepatrol-sdk";
import { t } from "i18next";
import { tRiskFactor } from "../../coding/utils/score-utils";
import { AttributeUtils } from "../../coding/utils/attribute-utils";
import { HlaWayType } from "../../ra/models/HlaWay";
import { PresenceDistance } from "../../ra/models/HlaLane";

export interface NumberAttributeFilter {
  type: AttributeFilterType.Number;
  attributeName: AttributeName;
  max: number | null;
  min: number | null;
}

export interface TextAreaAttributeFilter {
  type: AttributeFilterType.TextArea;
  attributeName: AttributeName;
  value: boolean;
}

export interface SelectAttributeFilter {
  type: AttributeFilterType.Select;
  attributeName: AttributeName;
  value: string;
}

export type AttributeFilter =
  | NumberAttributeFilter
  | TextAreaAttributeFilter
  | SelectAttributeFilter;

export enum AttributeFilterType {
  Number = "Number",
  TextArea = "TextArea",
  Select = "Select",
}

export enum ScoreFilterType {
  RiskFactors = "RiskFactors",
  Limits = "Limits",
}

export type ScoreFilter = ScoreRiskFactorsFilter | ScoreLimitsFilter;

interface ScoreRiskFactorsFilter {
  type: ScoreFilterType.RiskFactors;
  riskFactors: RiskFactor[];
}

interface ScoreLimitsFilter {
  type: ScoreFilterType.Limits;
  limits: ResultsScoreLimits;
}

export interface ResultsScoreLimits {
  min: number;
  max: number;
}

export enum AttributeSorting {
  Alphabetic = "Alphabetic",
  ScoreImpact = "ScoreImpact",
}

export type ScoreFilters = {
  [key in keyof Score]?: ScoreFilter;
};

export type MinMaxFilter = { min: number | null; max: number | null };

export type HlaAttributeFilter = {
  type: HlaWayType[];
  heavyVehicleFlow: HeavyVehicleFlowType[];
  averageRoadSpeed: MinMaxFilter;
  averageDailyTraffic: MinMaxFilter;
  lit: boolean[];
  flowDirection: FlowDirectionType[];
  facilityType: FacilityType[];
  facilityWidthPerDirection: FacilityWidth[];
  lightSegregation: boolean[];
  tramOrTrainRails: boolean[];

  roadLane: (PresenceDistance | null)[];
  vehicleParking: (PresenceDistance | null)[];
  severeHazard: (PresenceDistance | null)[];
  objectOrLevelChange: (PresenceDistance | null)[];
  sidewalk: (PresenceDistance | null)[];
  numberOfLanes: (NumberOfLanesType | null)[];
  hardSegregation: boolean[];
};

export const emptyHlaAttributeFilter = (): HlaAttributeFilter => ({
  type: [],
  heavyVehicleFlow: [],
  averageRoadSpeed: { min: null, max: null },
  averageDailyTraffic: { min: null, max: null },
  lit: [],
  flowDirection: [],
  facilityType: [],
  facilityWidthPerDirection: [],
  lightSegregation: [],
  tramOrTrainRails: [],

  roadLane: [],
  vehicleParking: [],
  severeHazard: [],
  objectOrLevelChange: [],
  sidewalk: [],
  numberOfLanes: [],
  hardSegregation: [],
});

export interface ResultsFilters {
  osaWayFilters: string[];
  scoreFilters: ScoreFilters;
  treatmentFilters: TreatmentType[];
  attributeFilters: AttributeFilter[];
}

export const isScoreFiltersEmpty = (scoreFilters: ScoreFilters) => {
  return (
    !scoreFilters.global &&
    !scoreFilters.bicycleToBicycle &&
    !scoreFilters.bicycleToPedestrian &&
    !scoreFilters.singleBicycle &&
    !scoreFilters.vehicleToBicycle
  );
};

export const scoreFilterToString = (filter: ScoreFilter): string => {
  if (filter.type === ScoreFilterType.RiskFactors) {
    return filter.riskFactors.map((rf) => tRiskFactor(rf)).join(", ");
  }
  return t(`map:pieChart.filters.minMax`, {
    min: filter.limits.min.toFixed(2),
    max: filter.limits.max.toFixed(2),
  });
};

export const attributeFilterToString = (filter: AttributeFilter): string => {
  switch (filter.type) {
    case AttributeFilterType.Number: {
      return `${t(`map:pieChart.filters.minMax`, {
        min: filter.min || 0,
        max: filter.max || "∞",
      })} km/h`;
    }

    case AttributeFilterType.TextArea: {
      return filter.value
        ? t(`map:pieChart.textArea.filled`)
        : t(`map:pieChart.textArea.notFilled`);
    }

    case AttributeFilterType.Select: {
      return AttributeUtils.getOptionLabel(filter.value);
    }
  }
};
